import React, { Component } from 'react';

import './sass/App.scss';
import './utils/Marzipano/Marzipano.scss';
import InfoPanel from './components/InfoPanel';
import Minimap from './components/Minimap';
import Timeline from './components/Timeline';
import TimelineButton from './components/TimelineButton';
import TitleCard from './components/TitleCard';
import LinkNodes from './components/LinkNodes';
import Marzipano from './utils/Marzipano/Marzipano';
import { NodeData, InitialViewParameters } from './interfaces/NodeData';
import NetworkCalls from './utils/NetworkCalls';
import Dotenv from './utils/Dotenv';
import { HotspotDescription } from './interfaces/HotspotDescription';
import LevelSlider from './components/LevelSlider';
import { StylesProvider } from '@material-ui/core';
import { HashRouter as Router, Route } from 'react-router-dom';
import Media from './components/Media';
import Home from './components/Home';
import Faq from './components/Faq';
import { Sidebar } from './components/Sidebar';
import Documentation from './components/Documentation';
import About from './components/About';

interface IAppState {
  currDate: Date;
  currPanoId: string;
  currRotation: number;
  currViewParams: InitialViewParameters;
  floor: number;
  infoPanelId: string;
  infoPanelOpen: boolean;
  minimapEnlarged: boolean;
  nodesData: NodeData[];
  sideNavOpen: boolean;
  timelineOpen: boolean;
  linkNodeListOpen: boolean;
  hotspotDictionary: HotspotDescription[];
  minimapImage: string;
  currentNode: string;
}

export default class App extends Component<{}, IAppState> {
  private marzipano: Marzipano | undefined;
  private enableTimeline = Dotenv.ENABLE_TIMELINE;
  private config = Dotenv;
  // private abortController = new AbortController();
  private abortController: AbortController[] = [];
  private hotspotAbortController = new AbortController();
  private minimapImagesAbortController = new AbortController();

  public state = {
    currDate: new Date(Date.parse(Dotenv.INITIAL_DATE)),
    currPanoId: Dotenv.INITIAL_PANO_ID,
    currRotation: 0,
    currViewParams: {
      yaw: Dotenv.INITIAL_YAW,
      pitch: Dotenv.INITIAL_PITCH,
      fov: Dotenv.INITIAL_FOV,
    },
    floor: Dotenv.INITIAL_FLOOR,
    infoPanelId: '',
    infoPanelOpen: false,
    minimapEnlarged: false,
    nodesData: [],
    sideNavOpen: false,
    timelineOpen: false,
    linkNodeListOpen: false,
    hotspotDictionary: [],
    minimapImage: Dotenv.MINIMAP_IMAGE,
    currentNode: '',
  };

  private TimeLineMemo = React.memo(Timeline);
  private TimeLineButtonMemo = React.memo(TimelineButton);

  constructor(props: Readonly<{}>) {
    super(props);
    this.config = Dotenv;

    this.changeDate = this.changeDate.bind(this);
    this.changeFloor = this.changeFloor.bind(this);
    this.changeTimelineOpen = this.changeTimelineOpen.bind(this);
    this.changeInfoPanelOpen = this.changeInfoPanelOpen.bind(this);
    this.getInfoHotspot = this.getInfoHotspot.bind(this);
    this.minimapClick = this.minimapClick.bind(this);
    this.infoNodeClick = this.infoNodeClick.bind(this);
    this.updateCurrPano = this.updateCurrPano.bind(this);
    this.updateRotation = this.updateRotation.bind(this);
    this.updateMinimapEnlarged = this.updateMinimapEnlarged.bind(this);
    this.updateSideNavOpen = this.updateSideNavOpen.bind(this);
    this.updateViewParams = this.updateViewParams.bind(this);
    this.updateLinkNodes = this.updateLinkNodes.bind(this);
    this.handleDropdownClick = this.handleDropdownClick.bind(this);
    this.getMinimapImage = this.getMinimapImage.bind(this);
    this.rerenderCallback = this.rerenderCallback.bind(this);
  }

  private updateCurrPano(panoId: string): void {
    this.setState({ currPanoId: panoId });
    this.getHotspotDescriptionList(panoId);
  }

  // Clicking on an info node switches view toward that node, and opens info panel
  private infoNodeClick(infoYaw: number, infoPitch: number, infoId: string): void {
    // Updated view params
    const viewParams = {
      ...this.state.currViewParams,
      pitch: infoPitch,
      yaw: infoYaw,
    };

    // Check marzipano is not undefined
    if (!this.marzipano) throw new Error('Marzipano is undefined.');
    this.marzipano.panUpdateCurrView(viewParams, this.marzipano?.findSceneById(this.state.currPanoId));
    this.updateViewParams(viewParams);

    // Open info panel
    this.getInfoHotspot(infoId);
  }

  private minimapClick(panoId: string): void {
    this.setState({ currPanoId: panoId });
    if (this.marzipano) this.marzipano.switchScene(this.marzipano.findSceneById(panoId));
  }

  private updateRotation(rotation: number): void {
    this.setState({ currRotation: rotation });
  }

  private updateViewParams(viewParams: InitialViewParameters): void {
    this.setState({ currViewParams: viewParams });
  }

  private rerenderCallback() {
    this.forceUpdate();
  }

  private getSurveyNodes(): void {
    const viewParams = this.state.currViewParams;
    var a = document.getElementById('#pano');
    if (a != null) {
      a.remove();
    }
    // document.getElementById("#pano").remove();
    if (this.marzipano) {
      this.marzipano.viewer.domElement().innerHTML = '';
      this.marzipano.viewer.destroyAllScenes();
      // this.abortController.abort();
      if (this.abortController.length > 1) {
        this.abortController[this.abortController.length - 1].abort();
        this.abortController.pop();
      }
    }
    this.abortController.push(new AbortController());
    NetworkCalls.fetchSurveyNodes(
      this.state.floor,
      this.state.currDate,
      this.abortController[this.abortController.length - 1],
    ).then(nodesData => {
      if (!nodesData || !nodesData.length) return;

      this.setState({ nodesData });

      // Get correct minimap image on initial load.
      // this.getMinimapImage(this.state.floor);

      this.marzipano = new Marzipano(
        this.state.nodesData,
        this.getInfoHotspot,
        this.updateCurrPano,
        this.updateRotation,
        this.updateViewParams,
        this.changeInfoPanelOpen,
        this.config,
      );

      // Get current tile id based on previous node number
      let currentTilesId = nodesData[0].minimapNode.tilesId;
      let xDifference = 10000;
      let yDifference = 10000;
      const xOffset = this.state.currPanoId.split('_')[3];
      const yOffset = this.state.currPanoId.split('_')[4];
      for (let i = 0; i < nodesData.length; i++) {
        const node = nodesData[i];
        const xDiff = Math.abs(Number(xOffset) - node.xPixelOffset);
        const yDiff = Math.abs(Number(yOffset) - node.yPixelOffset);
        if (node.xPixelOffset.toString() === xOffset && node.yPixelOffset.toString() === yOffset) {
          currentTilesId = node.minimapNode.tilesId;
          break;
        } else if (xDifference > xDiff && yDifference > yDiff) {
          xDifference = xDiff;
          yDifference = yDiff;
          currentTilesId = node.minimapNode.tilesId;
        }
      }

      this.minimapClick(
        nodesData.some((e: any) => e.minimapNode.tilesId === this.state.currPanoId)
          ? this.state.currPanoId
          : currentTilesId,
      );

      if (viewParams.fov !== 0 || viewParams.pitch !== 0 || viewParams.yaw !== 0) {
        if (this.marzipano && this.marzipano.findSceneById(this.state.currPanoId)) {
          this.marzipano.updateCurrView(viewParams, this.marzipano.findSceneById(this.state.currPanoId));
        }
      }
    });
  }

  public componentDidMount(): void {
    this.getSurveyNodes();
  }

  private changeDate(date: Date, callback: Function): void {
    this.setState({ currDate: date }, () => {
      this.getSurveyNodes();
      callback();
    });
  }

  public shouldComponentUpdate(nextProps: any, nextState: IAppState): boolean {
    if (
      nextState.currViewParams.pitch !== this.state.currViewParams.pitch ||
      nextState.currViewParams.fov !== this.state.currViewParams.fov ||
      nextState.currViewParams.yaw !== this.state.currViewParams.yaw ||
      nextState.currRotation !== this.state.currRotation
    ) {
      return false;
    }

    // makes camera butter smooth but potentially breaks things
    // if(
    //   this.state.currDate != nextState.currDate ||
    //   this.state.currPanoId != nextState.currPanoId ||
    //   this.state.floor != nextState.floor ||
    //   this.state.infoPanelId != nextState.infoPanelId ||
    //   this.state.minimapEnlarged != nextState.minimapEnlarged ||
    //   this.state.nodesData != nextState.nodesData ||
    //   this.state.sideNavOpen != nextState.sideNavOpen ||
    //   this.state.timelineOpen != nextState.timelineOpen ||
    //   this.state.linkNodeListOpen != nextState.linkNodeListOpen ||
    //   this.state.hotspotDictionary != nextState.hotspotDictionary ||
    //   this.state.minimapImage != nextState.minimapImage ||
    //   this.state.currentNode != nextState.currentNode

    //   ) {
    //   return true
    // }

    // else if(nextState.currViewParams.pitch === this.state.currViewParams.pitch ||
    //   nextState.currViewParams.fov === this.state.currViewParams.fov ||
    //   nextState.currViewParams.yaw === this.state.currViewParams.yaw ||
    //   nextState.currRotation === this.state.currRotation) {

    //     return false

    // }

    return true;
  }

  private updateMinimapEnlarged(minimapEnlarged: boolean): void {
    this.setState({ minimapEnlarged: minimapEnlarged });
  }

  private changeFloor(floor: number): void {
    this.setState({ floor: floor }, () => this.getSurveyNodes());
    this.getMinimapImage(floor);
  }

  private changeInfoPanelOpen(open: boolean): void {
    this.setState({ infoPanelOpen: open });
  }

  private changeTimelineOpen(open: boolean): void {
    this.setState({ timelineOpen: open });

    // Return minimap to normal when timeline is open
    // if (open) {
    //   this.updateMinimapEnlarged(!open);
    // }
  }

  private updateSideNavOpen(open: boolean): void {
    this.setState({ sideNavOpen: open });
  }

  // Loads all HotspotDescriptions for the current survey location.
  private async getHotspotDescriptionList(tilesId: string): Promise<void> {
    try {
      const res: HotspotDescription[] = await NetworkCalls.fetchHotspotDescription(
        tilesId,
        this.hotspotAbortController,
      );
      this.setState({ hotspotDictionary: res });
    } catch (e) {
      // Corrupted HotspotDescription, replace with empty.
      this.setState({ hotspotDictionary: [] });
    }
  }

  // Handles clicking on hotspot.
  private getInfoHotspot(infoId: string): void {
    this.setState({ infoPanelId: infoId });
    this.setState({ infoPanelOpen: true });
  }

  private getHotspotDescription(infoId: string): HotspotDescription | undefined {
    const res: HotspotDescription[] = this.state.hotspotDictionary ?? [];
    return res.find(x => x.infoId === infoId);
  }

  // Get current minimap image for the current floor.
  private async getMinimapImage(floor: number): Promise<void> {
    try {
      const res: any = await NetworkCalls.fetchMinimapImage(floor, this.minimapImagesAbortController);
      this.setState({ minimapImage: res });
    } catch (e) {
      // Corrupted MinimapImage, replace with empty.
      this.setState({ minimapImage: '' });
    }
  }

  // Update link node menu (open or close)
  private updateLinkNodes(state: boolean): void {
    this.setState({ linkNodeListOpen: state });
  }

  // Handles link/info hotspot dropdown click
  private handleDropdownClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>, state: boolean): void {
    e.stopPropagation();
    // Close minimap on dropdown click
    this.updateMinimapEnlarged(false);
    // Open or close menu if not already
    this.updateLinkNodes(state);
  }

  public render(): React.ReactElement {
    return (
      <StylesProvider injectFirst>
        <Router>
          <div className="App" style={{ display: 'flex' }}>
            <TitleCard
              firstLineName={this.config.TITLE} //{this.marzipano?.findNameById(this.state.currPanoId) ?? ""}
              timelineOpen={this.state.timelineOpen}
            />
            {document.location.toString().endsWith('site') && (
              <div
                className="linkButton"
                onClick={(e): void => this.handleDropdownClick(e, !this.state.linkNodeListOpen)}
              >
                {this.state.linkNodeListOpen ? (
                  <i className="fas fa-chevron-up" />
                ) : (
                  <i className="fas fa-chevron-down" />
                )}
              </div>
            )}
            <LinkNodes
              linkNodes={this.marzipano?.findLinkNodesById(this.state.currPanoId) ?? ''}
              infoNodes={this.marzipano?.findInfoNodesById(this.state.currPanoId) ?? ''}
              timelineOpen={this.state.timelineOpen}
              onLinkNodeClick={this.minimapClick}
              onInfoNodeClick={this.infoNodeClick}
              listOpen={this.state.linkNodeListOpen}
              updateLinkNodes={this.updateLinkNodes}
            />
            {this.enableTimeline && (
              <this.TimeLineButtonMemo
                timelineOpen={this.state.timelineOpen}
                changeTimelineOpen={this.changeTimelineOpen}
              />
            )}
            <Sidebar rerenderCallback={this.rerenderCallback} />
            <hr className="timelineIndicator" />
            {this.state.infoPanelOpen && (
              <InfoPanel
                sideNavOpen={this.state.sideNavOpen}
                infoPanelOpen={this.state.infoPanelOpen}
                changeInfoPanelOpen={this.changeInfoPanelOpen}
                hotspotDescription={this.getHotspotDescription(this.state.infoPanelId)}
              />
            )}

            <div id="pano" onClick={(e): void => this.handleDropdownClick(e, false)} />
            <Minimap
              currPanoId={this.state.currPanoId}
              onClickNode={this.minimapClick}
              currRotation={this.state.currRotation}
              minimapEnlarged={this.state.minimapEnlarged}
              updateMinimapEnlarged={this.updateMinimapEnlarged}
              nodeData={this.state.nodesData}
              sideNavOpened={this.state.sideNavOpen}
              config={this.config}
              linkNodeFunction={this.updateLinkNodes}
              timelineOpen={this.state.timelineOpen}
              closeTimelineFunction={this.changeTimelineOpen}
              minimapImage={this.state.minimapImage}
              floor={this.state.floor}
            />
            {this.config.ENABLE_FLOORS && (
              <LevelSlider
                timelineOpen={this.state.timelineOpen}
                floor={this.state.floor}
                changeFloor={this.changeFloor}
                date={this.state.currDate}
              />
            )}
            {this.config.ENABLE_TIMELINE && (
              <this.TimeLineMemo
                timelineOpen={this.state.timelineOpen}
                floor={this.state.floor}
                changeFloor={this.changeFloor}
                date={this.state.currDate}
                changeDate={this.changeDate}
                closeTimelineFunction={this.changeTimelineOpen}
              />
            )}
            <Route exact path="/" render={(): React.ReactElement => <Home />} />
            <Route path="/site" render={(): React.ReactElement => <div />} />
            {this.config.ENABLE_MEDIA && (
              <Route
                path="/media"
                render={(props: any): React.ReactElement => <Media {...props} BASE_URL={this.config.BASE_URL} />}
              />
            )}
            {this.config.ENABLE_DOCUMENTATION && (
              <Route path="/documentation" render={(): React.ReactElement => <Documentation />} />
            )}
            {this.config.ENABLE_FAQ && <Route path="/faq" component={Faq} />}
            <Route path="/about" render={(): React.ReactElement => <About />} />
          </div>
        </Router>
      </StylesProvider>
    );
  }
}
